/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GtoUom } from '@gh/builder-shared-data';
import {
  createDefaultGridModel,
  dictionaryValueFormatter,
  EDITABLE_CELL_GREEN_CLASS,
  likeEditableCellRenderer,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { EnumDictionary, getRouteInitData, NumberTypes, State } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildEditableQuantityColumn,
  buildNumberColumn,
  gridCollectionComparator,
  GridModel,
  GridOptionsExt,
  ManualGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { ColDef } from 'ag-grid-community';
import { isNil } from 'lodash';
import {
  getHpeTopId,
  getHpeTopQuantityType,
  HpeTop,
  hpeTopFieldState,
  validateHpeTopField,
} from '../../../resources/hpe-details.model';
import { HpeCommandService } from '../hpe-command.service';
import { HpeDetailsState, hpeTopComparator, queryHpeEditable } from '../hpe-details.state';

@Injectable()
export class HpeDetailsTopGridDefinition extends ManualGridDefinitionWithModel {
  gtoUomDict: EnumDictionary<GtoUom>;

  constructor(private route: ActivatedRoute,
              private translationService: TranslationService,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService) {
    super();

    this.gtoUomDict = getRouteInitData(route)['gtoUomDict'];
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel<HpeTop>({
      editable: this.state.project(queryHpeEditable),
      getId: getHpeTopId,
      validate: validateHpeTopField,
      getCellDef: hpeTopFieldState,
      command: (entity, name, value) => this.commands.topSetField(getHpeTopId(entity), name, value),
    });
  }

  buildGridOptions(): GridOptionsExt {
    return {
      defaultColDef: {
        sortable: false,
      },
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: HpeTop }) => !isNil(data.dirtyType),
      },
    };
  }

  buildColumnDefs(): ColDef[] {
    return [{
      field: 'code',
      sort: 'asc',
      comparator: gridCollectionComparator(hpeTopComparator),
      headerName: this.translationService.get('lbl_code'),
      width: 150,
    }, {
      field: 'value',
      headerName: this.translationService.get('lbl_value'),
      width: 90,
      ...buildEditableQuantityColumn(getHpeTopQuantityType, this.model.getCellAccessor('value')),
    }, {
      field: 'gtoUom',
      headerName: this.translationService.get('lbl_uom_abbreviation'),
      width: 70,
      valueFormatter: dictionaryValueFormatter(this.gtoUomDict),
    }, {
      field: 'count',
      headerName: this.translationService.get('lbl_count'),
      width: 65,
      ...buildNumberColumn(NumberTypes.Quantity),
      cellRenderer: likeEditableCellRenderer,
      cellClassRules: {
        [EDITABLE_CELL_GREEN_CLASS]: ({ data }: { data: HpeTop }) => data.isFromGto,
      },
    }];
  }
}
